import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  Button,
  Grid,
  Chip,
  CircularProgress,
  Alert,
  Divider
} from '@mui/material';
import { platformApiFetch } from './platformApi';

function statusColor(s) {
  if (s === 'active') return 'success';
  if (s === 'archived') return 'default';
  return 'warning';
}

export default function PlatformTenantRoleDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [role, setRole] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await platformApiFetch(`/api/platform/tenant-roles/${id}`);
      const json = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(json.error || 'Not found');
      setRole(json.role);
    } catch (e) {
      setError(e.message);
      setRole(null);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    load();
  }, [load]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error && !role) {
    return <Alert severity="error">{error}</Alert>;
  }

  if (!role) return null;

  const row = (label, value) => (
    <Grid item xs={12} sm={6}>
      <Typography variant="caption" color="text.secondary">
        {label}
      </Typography>
      <Typography variant="body1">{value ?? '—'}</Typography>
    </Grid>
  );

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
        <Box>
          <Typography variant="h4">{role.role_name || role.name}</Typography>
          <Typography variant="body2" color="text.secondary">
            {role.tenant_name} · <code>{role.tenant_code}</code>
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button variant="outlined" onClick={() => navigate('/platform/tenant-roles')}>
            Back to list
          </Button>
          <Button variant="outlined" onClick={() => navigate(`/platform/tenants/${role.tenant_id}`)}>
            Tenant
          </Button>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Paper sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
          <Chip label={role.status} color={statusColor(role.status)} />
          {role.is_system_role || role.is_system ? (
            <Chip label="System role" />
          ) : (
            <Chip label="Custom" variant="outlined" />
          )}
        </Box>
        <Grid container spacing={2}>
          {row('Role id', role.id)}
          {row('Role code', role.role_code || role.slug)}
          {row('Description', role.description)}
          {row('Users assigned', role.user_count != null ? String(role.user_count) : '—')}
          {row('Created', role.created_at ? String(role.created_at) : '—')}
          {row('Updated', role.updated_at ? String(role.updated_at) : '—')}
        </Grid>
        <Divider sx={{ my: 2 }} />
        <Typography variant="subtitle1" gutterBottom>
          Permissions
        </Typography>
        <Typography variant="body2" component="div" sx={{ whiteSpace: 'pre-wrap' }}>
          {(role.permissions || []).length ? (role.permissions || []).join('\n') : '—'}
        </Typography>
      </Paper>
    </Box>
  );
}
